import { useState, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import type { AcceptDocumentResponse, RejectDocumentResponse } from "../types";

export type BatchAction = "accept" | "reject";

export interface BatchProgress {
  total: number;
  completed: number;
  succeeded: number;
  failed: number;
  currentDocumentId: string | null;
  results: BatchResult[];
}

export interface BatchResult {
  documentId: string;
  folio: number;
  success: boolean;
  error?: string;
}

export interface UseAcceptRejectDocumentsResult {
  isProcessing: boolean;
  progress: BatchProgress | null;
  acceptDocument: (documentId: string) => Promise<AcceptDocumentResponse>;
  rejectDocument: (
    documentId: string,
    reason: string,
  ) => Promise<RejectDocumentResponse>;
  processBatch: (
    documentIds: string[],
    folios: number[],
    action: BatchAction,
    reason?: string,
  ) => Promise<BatchProgress>;
  reset: () => void;
}

export function useAcceptRejectDocuments(): UseAcceptRejectDocumentsResult {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<BatchProgress | null>(null);

  const acceptDocument = useCallback(
    async (documentId: string): Promise<AcceptDocumentResponse> => {
      return apiFetch.post<AcceptDocumentResponse>(
        `/api/received-documents/${documentId}/accept`,
      );
    },
    [],
  );

  const rejectDocument = useCallback(
    async (
      documentId: string,
      reason: string,
    ): Promise<RejectDocumentResponse> => {
      return apiFetch.post<RejectDocumentResponse>(
        `/api/received-documents/${documentId}/reject`,
        { reason },
      );
    },
    [],
  );

  const processBatch = useCallback(
    async (
      documentIds: string[],
      folios: number[],
      action: BatchAction,
      reason?: string,
    ): Promise<BatchProgress> => {
      setIsProcessing(true);
      const initialProgress: BatchProgress = {
        total: documentIds.length,
        completed: 0,
        succeeded: 0,
        failed: 0,
        currentDocumentId: null,
        results: [],
      };
      setProgress(initialProgress);

      const results: BatchResult[] = [];

      for (let i = 0; i < documentIds.length; i++) {
        const documentId = documentIds[i]!;
        const folio = folios[i]!;

        setProgress((prev) =>
          prev ? { ...prev, currentDocumentId: documentId } : prev,
        );

        try {
          if (action === "accept") {
            await acceptDocument(documentId);
          } else {
            await rejectDocument(documentId, reason!);
          }

          results.push({ documentId, folio, success: true });

          setProgress((prev) =>
            prev
              ? {
                  ...prev,
                  completed: prev.completed + 1,
                  succeeded: prev.succeeded + 1,
                  results: [...results],
                }
              : prev,
          );
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : "Error desconocido";
          results.push({
            documentId,
            folio,
            success: false,
            error: errorMessage,
          });

          setProgress((prev) =>
            prev
              ? {
                  ...prev,
                  completed: prev.completed + 1,
                  failed: prev.failed + 1,
                  results: [...results],
                }
              : prev,
          );
        }
      }

      const finalProgress: BatchProgress = {
        total: documentIds.length,
        completed: documentIds.length,
        succeeded: results.filter((r) => r.success).length,
        failed: results.filter((r) => !r.success).length,
        currentDocumentId: null,
        results,
      };

      setProgress(finalProgress);
      setIsProcessing(false);

      return finalProgress;
    },
    [acceptDocument, rejectDocument],
  );

  const reset = useCallback(() => {
    setIsProcessing(false);
    setProgress(null);
  }, []);

  return {
    isProcessing,
    progress,
    acceptDocument,
    rejectDocument,
    processBatch,
    reset,
  };
}
